/**
 * Обёртка над API мини-аппов Telegram.
 *
 * Приложение должно открываться и в обычном браузере — иначе его невозможно
 * разрабатывать и отлаживать. Поэтому все обращения к Telegram идут через эти
 * функции, а при отсутствии вебвью они просто ничего не делают.
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name: string } };
  colorScheme: "light" | "dark";
  viewportStableHeight: number;
  isExpanded: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  disableVerticalSwipes?: () => void;
  requestFullscreen?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent: (event: string, handler: () => void) => void;
  offEvent: (event: string, handler: () => void) => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  openInvoice?: (
    url: string,
    callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void,
  ) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export const isInsideTelegram = (): boolean => Boolean(getWebApp()?.initData);

/** Подготавливает вебвью: разворачивает на весь экран и убирает свайп-закрытие. */
export function initTelegram(): void {
  const app = getWebApp();
  if (!app) return;

  app.ready();
  app.expand();

  // Без этого свайп вниз по игровому полю закрывает мини-апп прямо во время
  // раунда — игрок теряет ставку из-за жеста прокрутки
  app.disableVerticalSwipes?.();

  app.setHeaderColor?.("#07061a");
  app.setBackgroundColor?.("#07061a");
}

/** initData для входа. Пустая строка означает запуск вне Telegram. */
export function getInitData(): string {
  return getWebApp()?.initData ?? "";
}

type HapticStyle = "light" | "medium" | "heavy" | "soft" | "rigid";

export const haptic = {
  tap(style: HapticStyle = "light"): void {
    getWebApp()?.HapticFeedback?.impactOccurred(style);
  },
  success(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred("success");
  },
  error(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred("error");
  },
  select(): void {
    getWebApp()?.HapticFeedback?.selectionChanged();
  },
};

/** Нативный счёт Telegram Stars внутри мини-аппа. */
export function openStarsInvoice(
  url: string,
): Promise<"paid" | "cancelled" | "failed" | "pending"> {
  const app = getWebApp();
  if (!app?.openInvoice) {
    window.open(url, "_blank");
    return Promise.resolve("pending");
  }

  return new Promise((resolve) => {
    app.openInvoice!(url, (status) => resolve(status));
  });
}

/** Счёт CryptoBot: открывается их мини-аппом, если ссылка telegram. */
export function openChannel(url: string): void {
  const app = getWebApp();
  if (app?.openTelegramLink && url.startsWith("https://t.me/")) {
    app.openTelegramLink(url);
    return;
  }
  window.open(url, "_blank");
}

export function openCryptoInvoice(url: string): void {
  const app = getWebApp();
  if (url.startsWith("https://t.me/") && app?.openTelegramLink) {
    app.openTelegramLink(url);
    return;
  }
  if (app?.openLink) {
    app.openLink(url);
    return;
  }
  window.open(url, "_blank");
}

/**
 * Высота видимой области вебвью. Отличается от innerHeight: в Telegram клавиатура
 * и системные панели меняют её, а вёрстка должна подстраиваться.
 */
export function useViewportHeight(): void {
  const app = getWebApp();
  const apply = () => {
    const height = app?.viewportStableHeight ?? window.innerHeight;
    document.documentElement.style.setProperty("--viewport-height", `${height}px`);
  };

  apply();

  if (app) {
    app.onEvent("viewportChanged", apply);
  } else {
    window.addEventListener("resize", apply);
  }
}
